import React, { useCallback, useMemo } from 'react';

import {
  CustomerStatusEnum,
  CustomerInfoResponseModel,
  CustomerAdditionalInfoReplyRequestModel,
} from 'api/backofficeApiClient';
import {
  getProfileProgress,
  DEFAULT_ACTION_BTN_TEXT,
  StepChangeRequestModel,
  AdditionalInfoResponseModel,
  ProfileStatus,
} from 'utils/profileProgressUtils/generalProgressUtils';
import {
  completeClientProgressStep,
  hideCustomerPulsatingIndicator,
  sendCustomerAdditionalInfoResponse,
} from 'redux/reducers/clientSlice';
import {
  BaseProgressStep,
  BaseProgressPercentage,
  BaseProgressTrackerWrapper,
} from 'components/wrappers/BaseProgressBar';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { CustomerApproveStepModel } from 'api/adminApiClient';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { getCustomerProgressStages } from 'utils/profileProgressUtils/customerProgressUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './ClientProgressBar.module.less';

type Props = {
  client: CustomerInfoResponseModel;
  onChange: (client: CustomerInfoResponseModel) => void;
};

const progressStepErrorText = 'Waiting for admin response';

export default function ClientProgressBar(props: Props) {
  const { client, onChange } = props;
  const { customerId, stages } = client;
  const dispatch = useAppDispatch();
  const isAdminRole = useAppSelector(isAdminSelector);
  const customerStages = useMemo(() => {
    const stagesOptions = {
      isAdmin: isAdminRole,
      stages,
    };

    return getCustomerProgressStages(stagesOptions);
  }, [stages, isAdminRole]);

  const handleStepChange = useCallback(
    async (stageData: StepChangeRequestModel) => {
      const { stage, stageStatus, message: requestMessage } = stageData;

      const data = {
        customerId,
        currentStage: stage,
        currentStageStatus: stageStatus,
      } as CustomerApproveStepModel;

      requestMessage && (data.message = requestMessage);

      const result = await dispatch(completeClientProgressStep(data));

      if (completeClientProgressStep.fulfilled.match(result)) {
        !!requestMessage && BaseAlert.success('Request sent.');
        onChange(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
    },
    [customerId, dispatch, onChange],
  );

  const handleResponseSent = useCallback(
    async (responseInfo: AdditionalInfoResponseModel) => {
      if (!customerId) return;

      const { responseMessage, stage } = responseInfo;

      const data: CustomerAdditionalInfoReplyRequestModel = {
        customerId,
        additionalInfoReply: responseMessage,
        currentStage: stage as CustomerStatusEnum,
      };

      const result = await dispatch(sendCustomerAdditionalInfoResponse(data));

      if (sendCustomerAdditionalInfoResponse.fulfilled.match(result)) {
        BaseAlert.success('Response successfully sent.');
        onChange(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
    },
    [customerId, dispatch, onChange],
  );

  const onPulsatingHover = useCallback(
    async (stage: ProfileStatus) => {
      if (!customerId) return;

      const result = await dispatch(
        hideCustomerPulsatingIndicator({
          customerId,
          currentStage: stage as CustomerStatusEnum,
        }),
      );

      if (hideCustomerPulsatingIndicator.fulfilled.match(result)) {
        onChange(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
    },
    [customerId, dispatch, onChange],
  );

  return (
    <div className={styles.progressTracker}>
      <div className={styles.progressPercentage}>
        <BaseProgressPercentage percent={getProfileProgress(customerStages)} />
      </div>

      <div className="page-box">
        <div className="page-inner-container">
          <BaseProgressTrackerWrapper>
            {customerStages.map((progressStep, index) => (
              <BaseProgressStep
                key={index}
                index={index}
                isAdmin={isAdminRole}
                {...progressStep}
                errorText={
                  progressStep.errorText || isAdminRole
                    ? undefined
                    : progressStepErrorText
                }
                onComplete={handleStepChange}
                actionBtnText={
                  progressStep.actionBtnText || DEFAULT_ACTION_BTN_TEXT
                }
                isActionEnabled={progressStep.isActionEnabled || false}
                onRequestSent={handleStepChange}
                onResponseSent={handleResponseSent}
                onPulsatingHover={onPulsatingHover}
              />
            ))}
          </BaseProgressTrackerWrapper>
        </div>
      </div>
    </div>
  );
}
